import { GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId } from 'api/campaignV2/types/GetMyCampaignOfferByCampaignId';
import type { GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers } from 'api/campaignV2/types/GetMyCampaignOfferByCampaignId';
import { GetMyCampaignOffers_getMyCampaignOffers_entities } from 'api/campaignV2/types/GetMyCampaignOffers';
import {
  CampaignOfferAttention,
  CampaignType,
  CounterOfferProposalStatus,
} from 'api/graphql-global-types';
import { CampaignVersion } from 'constants/deliverables';

export const getCounterOfferText = (
  counterOfferAttention?: CampaignOfferAttention | null
) => {
  if (counterOfferAttention === CampaignOfferAttention.TALENT) {
    return 'Counter Received';
  } else if (counterOfferAttention === CampaignOfferAttention.BRAND) {
    return 'Counter Submitted';
  }

  return 'Counter Pending';
};

// Only for Social V3 and rest of the campaigns
export const hasCounterOffer = (
  offer?: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId | null
) => {
  const isCampaignV3 = offer?.campaign?.v === CampaignVersion.V3;
  // Direct Booking is grouped with Custom here (see helpers/campaign
  // isCustomLikeCampaign); inlined to avoid a circular import.
  const isSocial =
    offer?.campaign?.type === CampaignType.Social ||
    offer?.campaign?.type === CampaignType.Custom ||
    offer?.campaign?.type === CampaignType.DirectBooking;

  return isCampaignV3 || (!isCampaignV3 && !isSocial);
};

export const isCounterSent = (
  offer?: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId | null
) => {
  return (
    hasCounterOffer(offer) &&
    offer?.counterOfferAttention === CampaignOfferAttention.BRAND
  );
};

export const isCounterReceived = (
  offer?: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId | null
) => {
  return (
    hasCounterOffer(offer) &&
    offer?.counterOfferAttention === CampaignOfferAttention.TALENT
  );
};

export const getLastCounterOffer = (
  offer?:
    | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId
    | GetMyCampaignOffers_getMyCampaignOffers_entities
    | null
) => {
  // Backend will sort by createdAt descending, so this will always be the last counter offer, but filter just for safety
  return offer?.counterOffers?.filter(
    (counterOffer) => counterOffer.status === CounterOfferProposalStatus.PENDING
  )?.[0];
};

/**
 * Groups counter offers into pairs (counter offer + response).
 * Backend sorts by createdAt descending (most recent first).
 * We reverse to get chronological order (oldest first), then pair them.
 * Each counter offer shows the next one (chronologically newer) as its response.
 * If a counter offer doesn't have a response, it was sent by athlete (athlete always initiates),
 * so we still show it.
 * We don't show items twice (if used as response, don't show as counter).
 *
 * @param counterOffers - Array of counter offers (sorted descending by createdAt)
 */
export const groupCounterOffersIntoPairs = (
  counterOffers:
    | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers[]
    | null
): {
  counterOffer: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers;
  response: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers | null;
  counterNumber: number;
}[] => {
  if (!counterOffers || counterOffers.length === 0) {
    return [];
  }

  // Reverse to get chronological order (oldest first)
  const chronologicalOffers = [...counterOffers].reverse();

  const pairs: {
    counterOffer: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers;
    response: GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_counterOffers | null;
    counterNumber: number;
  }[] = [];
  const usedAsResponse = new Set<string>();
  let counterNumber = 1;

  // Pair each counter offer with the next one (chronologically newer) as its response
  for (let i = 0; i < chronologicalOffers.length; i++) {
    const counterOffer = chronologicalOffers[i];
    const response =
      i + 1 < chronologicalOffers.length ? chronologicalOffers[i + 1] : null;

    // Skip if this counter offer was already used as a response
    if (usedAsResponse.has(counterOffer.id)) {
      continue;
    }

    // If there's a response, mark it as used
    if (response) {
      usedAsResponse.add(response.id);
    }

    // Always add pairs (if no response, it's athlete-initiated so we show it)
    pairs.push({
      counterOffer,
      response,
      counterNumber: counterNumber++,
    });
  }

  return pairs;
};
